import gql from "graphql-tag";
import { visit, type DocumentNode } from "graphql";

// The `media` field of the Hooligans product-images module (OCMOD 0.2.0,
// contract shared/opencart-tools/hooligans-product-images/PHASE5A-API-MEDIA.md):
// the canonical renditions of one product photo under /image/r/v1/. It is null
// whenever the module cannot vouch for the photo OpenCart serves now, and the
// legacy fields (`image`, `imageBig`) stay the image data in that case.
//
// Rules every consumer follows (SPEC §2–§4, phase-6 plan §5 D1–D6):
//   - the photo of a slot comes from that slot's own `media` or from its own
//     legacy fields, never from another slot's `media`;
//   - `src` is used verbatim: never rebuilt from a key or re-encoded;
//   - a value that does not have the contract's shape counts as null;
//   - the field is selected only while the API schema has it (the gate below),
//     because an unknown field fails the whole GraphQL request.

export type ProductMediaRendition = { src: string; w: number; h: number };

export type ProductMedia = {
  w: number;
  h: number;
  alpha: boolean;
  lqip: string | null;
  avif: ProductMediaRendition[];
  jpeg: ProductMediaRendition;
  share: ProductMediaRendition | null;
};

/** What a `<picture>` needs: an AVIF ladder and a JPEG `<img>` fallback. */
export type ProductMediaPicture = {
  avifSrcset: string;
  jpegSrc: string;
  jpegSrcset: string;
  width: number;
  height: number;
  lqip: string | null;
};

/** Selection for surfaces that render the full ladder (the PDP). */
export const PRODUCT_MEDIA_FIELDS =
  "media { w h alpha lqip avif { src w h } jpeg { src w h } share { src w h } }";

/**
 * Selection for product cards and lists (phase-6 plan D7): the ladder and the
 * JPEG with their sizes (the normalizer needs `h`), without `alpha`, `lqip`
 * and `share`. About 1.3 KB raw and 50 B gzip per photo.
 */
export const PRODUCT_CARD_MEDIA_FIELDS =
  "media { w h avif { src w h } jpeg { src w h } }";

const MAX_DIMENSION = 100000;
const MAX_RUNGS = 16;
const MAX_SRC_LENGTH = 2048;
const LQIP_PREFIX = "data:image/webp;base64,";
const MAX_LQIP_PAYLOAD = 2800;

const isDimension = (value: unknown): value is number =>
  typeof value === "number" &&
  Number.isInteger(value) &&
  value > 0 &&
  value <= MAX_DIMENSION;

// An absolute http(s) URL that a srcset can carry as one candidate: no
// whitespace or comma (the srcset separators) and nothing an attribute or a
// CSS url() would need escaped. The module's encoding (encodeURIComponent per
// segment) never produces any of these.
const isSrc = (value: unknown): value is string =>
  typeof value === "string" &&
  value.length <= MAX_SRC_LENGTH &&
  /^https?:\/\/[^\s,"<>\\]+$/i.test(value);

const normalizeRendition = (value: unknown): ProductMediaRendition | null => {
  if (!value || typeof value !== "object") return null;
  const { src, w, h } = value as Record<string, unknown>;
  if (!isSrc(src) || !isDimension(w) || !isDimension(h)) return null;
  return { src, w, h };
};

/**
 * The `lqip` member as the contract allows it (an inline WebP data URI of
 * bounded size), or null. Also used where `lqip` is selected on its own,
 * outside a whole `media` value (utils/catalogCardLqip).
 */
export const normalizeLqip = (value: unknown): string | null => {
  if (typeof value !== "string" || !value.startsWith(LQIP_PREFIX)) return null;
  const payload = value.slice(LQIP_PREFIX.length);
  return payload.length > 0 &&
    payload.length <= MAX_LQIP_PAYLOAD &&
    /^[A-Za-z0-9+/]+={0,2}$/.test(payload)
    ? value
    : null;
};

const normalized = new WeakMap<object, ProductMedia | null>();

/**
 * The `media` value in the contract's shape, or null. Fail-safe: anything
 * malformed in the ladder or the JPEG fallback discards the whole value (the
 * slot then shows its legacy photo); a malformed `lqip` or `share` only
 * drops that member.
 */
export function normalizeProductMedia(value: unknown): ProductMedia | null {
  if (!value || typeof value !== "object") return null;
  const cached = normalized.get(value as object);
  if (cached !== undefined) return cached;

  const raw = value as Record<string, unknown>;
  let result: ProductMedia | null = null;
  const avif = Array.isArray(raw.avif)
    ? raw.avif.slice(0, MAX_RUNGS + 1).map(normalizeRendition)
    : [];
  const ascending = avif.every(
    (rung, index) =>
      rung !== null && (index === 0 || rung.w > (avif[index - 1] as any).w)
  );
  const jpeg = normalizeRendition(raw.jpeg);
  if (
    isDimension(raw.w) &&
    isDimension(raw.h) &&
    avif.length > 0 &&
    avif.length <= MAX_RUNGS &&
    ascending &&
    jpeg
  ) {
    result = {
      w: raw.w,
      h: raw.h,
      alpha: raw.alpha === true,
      lqip: normalizeLqip(raw.lqip),
      avif: avif as ProductMediaRendition[],
      jpeg,
      share: normalizeRendition(raw.share),
    };
  }
  normalized.set(value as object, result);
  return result;
}

/**
 * `<picture>` sources for a slot: every AVIF rung with its width descriptor,
 * and the JPEG fallback as a two-rung srcset (`w640.jpg`, plus `share.jpg`
 * when it is wider) so a Retina browser without AVIF still gets a sharp
 * photo (SPEC §3, binding phase-6 note). Null when the slot has no usable
 * `media`.
 */
export function productMediaPicture(
  value: unknown
): ProductMediaPicture | null {
  const media = normalizeProductMedia(value);
  if (!media) return null;
  const jpegCandidates = [media.jpeg];
  if (media.share && media.share.w > media.jpeg.w)
    jpegCandidates.push(media.share);
  return {
    avifSrcset: media.avif.map((rung) => `${rung.src} ${rung.w}w`).join(", "),
    jpegSrc: media.jpeg.src,
    jpegSrcset: jpegCandidates
      .map((rendition) => `${rendition.src} ${rendition.w}w`)
      .join(", "),
    width: media.w,
    height: media.h,
    lqip: media.lqip,
  };
}

/**
 * The smallest AVIF rung at least `minWidth` pixels wide (the largest one
 * when none is), for fixed-size thumbnails; "" without usable `media`.
 */
export function productMediaRungSrc(value: unknown, minWidth: number): string {
  const media = normalizeProductMedia(value);
  if (!media) return "";
  const rung =
    media.avif.find((candidate) => candidate.w >= minWidth) ||
    media.avif[media.avif.length - 1];
  return rung.src;
}

/** The universally decodable JPEG of a slot (`w640.jpg`); "" without `media`. */
export function productMediaJpegSrc(value: unknown): string {
  return normalizeProductMedia(value)?.jpeg.src || "";
}

const SHARE_ORIGIN = "https://cdn.hooliganseyewear.com";
const SHARE_PATH_PREFIX = "/image/r/v1/";

/**
 * The slot's `share.jpg` (longest side ≤ 1600, flattened JPEG, stable URL
 * without `?v`) for consumers outside the page: og:image, JSON-LD, Klaviyo
 * (phase 7, SPEC P6). Only a `share.jpg` of the CDN renditions tree counts
 * (the same rule as server/server-product-media.js); null without usable
 * `media` or without such a `share`, and the consumer keeps the slot's
 * legacy photo.
 */
export function productMediaShare(
  value: unknown
): ProductMediaRendition | null {
  const share = normalizeProductMedia(value)?.share;
  if (!share) return null;
  try {
    const parsed = new URL(share.src);
    return parsed.origin === SHARE_ORIGIN &&
      parsed.pathname.startsWith(SHARE_PATH_PREFIX) &&
      /\/share\.jpg$/.test(parsed.pathname) &&
      !parsed.search &&
      !parsed.hash
      ? share
      : null;
  } catch {
    return null;
  }
}

/**
 * A compact copy for stores that outlive the page (recently viewed): the
 * ladder and the JPEG, without `lqip` and `share`. Null without usable media.
 */
export function compactProductMedia(value: unknown): ProductMedia | null {
  const media = normalizeProductMedia(value);
  if (!media) return null;
  return {
    w: media.w,
    h: media.h,
    alpha: media.alpha,
    lqip: null,
    avif: media.avif.map((rung) => ({ ...rung })),
    jpeg: { ...media.jpeg },
    share: null,
  };
}

// ---------------------------------------------------------------------------
// Schema gate (phase-6 plan D6)
// ---------------------------------------------------------------------------
// A request that selects a field the schema lacks fails as a whole, so a PDP
// query with `media` would break the page the moment the OCMOD is removed or a
// modification refresh drops it. `apollo/query` therefore sends `media` only
// while a cheap introspection probe (about 1 KB) says the schema has it:
// re-checked every minute on the server, kept for the browser session in the
// browser. A request refused with a `media` validation error within that window
// marks the gate closed and is sent again without the field at once. The same
// probe also answers for `media` on model-variants members (D5); the variants
// request reads that answer and never probes itself.

const SERVER_TTL_MS = 60 * 1000;
const BROWSER_TTL_MS = 30 * 60 * 1000;
const FAILURE_TTL_MS = 10 * 1000;
// A probe that has not answered by then counts as failed: every PDP query
// waits on it, and a stalled request must not hold them all.
const PROBE_TIMEOUT_MS = 3 * 1000;
const SESSION_KEY = "vf_product_media_gate";

export const PRODUCT_MEDIA_PROBE_QUERY = gql`
  query HooligansImageMediaProbe {
    product: __type(name: "Product") {
      fields {
        name
      }
    }
    image: __type(name: "productImage") {
      fields {
        name
      }
    }
    media: __type(name: "HooligansImageMedia") {
      fields {
        name
      }
    }
    rendition: __type(name: "HooligansImageRendition") {
      fields {
        name
      }
    }
    member: __type(name: "ModelVariantMember") {
      fields {
        name
      }
    }
  }
`;

const fieldNames = (type: any): Set<string> =>
  new Set(
    (Array.isArray(type?.fields) ? type.fields : [])
      .map((field: any) => field?.name)
      .filter((name: unknown) => typeof name === "string")
  );

const hasAll = (names: Set<string>, required: string[]) =>
  required.every((name) => names.has(name));

/** True when the introspection answer has every field the PDP selects. */
export function productMediaSchemaSupported(data: unknown): boolean {
  const answer = data as Record<string, unknown> | null;
  if (!answer || typeof answer !== "object") return false;
  return (
    fieldNames(answer.product).has("media") &&
    fieldNames(answer.image).has("media") &&
    hasAll(fieldNames(answer.media), [
      "w",
      "h",
      "alpha",
      "lqip",
      "avif",
      "jpeg",
      "share",
    ]) &&
    hasAll(fieldNames(answer.rendition), ["src", "w", "h"])
  );
}

/**
 * True when the answer also has `media` on the members of a model-variants
 * group (module model-variants 1.2.0, which adds it only next to this
 * module's types).
 */
export function variantMemberMediaSchemaSupported(data: unknown): boolean {
  const answer = data as Record<string, unknown> | null;
  return (
    productMediaSchemaSupported(data) && fieldNames(answer?.member).has("media")
  );
}

// `members`: the probe's answer for ModelVariantMember.media; undefined when
// this state does not know it (a session copy written before the field).
type GateState = { value: boolean; at: number; ttl: number; members?: boolean };

let gate: GateState | null = null;
let inflight: Promise<boolean> | null = null;
// A model-variants request with member `media` failed without a readable
// answer: leave the field out of that request until then (memory only).
let memberFailureUntil = 0;

const isBrowser = () => typeof window !== "undefined";

const readSessionGate = (): GateState | null => {
  if (!isBrowser()) return null;
  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(SESSION_KEY) || "null"
    );
    if (
      parsed &&
      typeof parsed.value === "boolean" &&
      typeof parsed.at === "number"
    )
      return {
        value: parsed.value,
        at: parsed.at,
        ttl: BROWSER_TTL_MS,
        members:
          typeof parsed.members === "boolean" ? parsed.members : undefined,
      };
  } catch {
    // Storage disabled or corrupt: probe again.
  }
  return null;
};

const writeSessionGate = (state: GateState) => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        value: state.value,
        at: state.at,
        members: state.members,
      })
    );
  } catch {
    // Non-critical: the gate is kept in memory for this page anyway.
  }
};

const setGate = (
  value: boolean,
  ttl: number,
  persist: boolean,
  members?: boolean
) => {
  gate = { value, at: Date.now(), ttl, members };
  if (persist) writeSessionGate(gate);
};

const isFresh = (state: GateState | null, now: number) =>
  !!state && now - state.at >= 0 && now - state.at < state.ttl;

const answerTtl = () => (isBrowser() ? BROWSER_TTL_MS : SERVER_TTL_MS);

/**
 * Whether queries may select `media` now. Never rejects: a failed probe
 * answers false (the legacy photo path) and is retried after a few seconds.
 */
export function resolveProductMediaSupport(client: any): Promise<boolean> {
  const now = Date.now();
  if (!gate) gate = readSessionGate();
  if (gate && isFresh(gate, now)) return Promise.resolve(gate.value);
  if (inflight) return inflight;
  if (typeof client?.query !== "function") return Promise.resolve(false);

  const probe = Promise.resolve().then(() =>
    client.query({
      query: PRODUCT_MEDIA_PROBE_QUERY,
      fetchPolicy: "no-cache",
    })
  );
  // A late answer after the timeout is ignored, never unhandled.
  probe.catch(() => undefined);
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error("media probe timed out")),
      PROBE_TIMEOUT_MS
    );
  });
  inflight = Promise.race([probe, timeout])
    .then((result: any) => {
      const supported = productMediaSchemaSupported(result?.data);
      setGate(
        supported,
        answerTtl(),
        true,
        variantMemberMediaSchemaSupported(result?.data)
      );
      return supported;
    })
    .catch(() => {
      setGate(false, FAILURE_TTL_MS, false);
      return false;
    })
    .finally(() => {
      if (timer) clearTimeout(timer);
      inflight = null;
    });
  return inflight;
}

/**
 * The gate's answer while it is fresh (this page's memory or the browser
 * session copy), else undefined. Never probes.
 */
export function knownProductMediaSupport(): boolean | undefined {
  if (!gate) gate = readSessionGate();
  return gate && isFresh(gate, Date.now()) ? gate.value : undefined;
}

/**
 * Whether a request may select `media` now. With `probe: false` it never
 * waits for the probe: an unknown gate counts as open, which is safe because
 * a refusal for the field is resent without it and closes the gate. Used on
 * paths that must not wait a round trip: the bag load awaited before the app
 * mounts, the bag mutations, the checkout start.
 */
const mediaAllowed = (
  client: any,
  options?: ProductMediaGateOptions
): Promise<boolean> =>
  options?.probe === false
    ? Promise.resolve(knownProductMediaSupport() !== false)
    : resolveProductMediaSupport(client);

export type ProductMediaGateOptions = { probe?: boolean };

/** A request was refused because the schema lacks `media`: close the gate. */
export function markProductMediaUnsupported(): void {
  setGate(false, answerTtl(), true);
}

/**
 * A request that selected `media` failed with an answer that could not be
 * read (the live connector dies with a var_dump on execution errors): keep
 * the gate closed briefly, as after a failed probe.
 */
export function markProductMediaFailure(): void {
  setGate(false, FAILURE_TTL_MS, false);
}

/**
 * Whether the model-variants request may select member `media` now: only
 * while a fresh probe answer says the members have it. Never probes; an
 * unknown answer counts as no and the tiles keep their legacy photo for that
 * request. On the server the page's product query has just probed; in the
 * browser a variants request that starts before the session's first probe
 * answer goes out without member media.
 */
export function knownVariantMemberMediaSupport(): boolean | undefined {
  if (!gate) gate = readSessionGate();
  if (!gate || !isFresh(gate, Date.now())) return undefined;
  if (!gate.value || Date.now() < memberFailureUntil) return false;
  return gate.members;
}

/**
 * The model-variants request was refused for member `media` (or failed with
 * it and passed without it): keep the rest of the probe's answer, members off
 * until the next probe. Only an open answer is changed: a closed one may be
 * the short in-memory state of a failure, which must never reach the session
 * copy.
 */
export function markVariantMemberMediaUnsupported(): void {
  if (!gate || !gate.value) return;
  gate = { ...gate, members: false };
  writeSessionGate(gate);
}

/** It failed without a readable answer: leave the field out briefly. */
export function markVariantMemberMediaFailure(): void {
  memberFailureUntil = Date.now() + FAILURE_TTL_MS;
}

/** Test hook: forget the gate (memory and session copy). */
export function resetProductMediaGate(): void {
  gate = null;
  inflight = null;
  memberFailureUntil = 0;
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

const MEDIA_TYPES =
  /^(?:Product|productImage|HooligansImageMedia|HooligansImageRendition)$/;
const UNKNOWN_FIELD =
  /^Cannot query field "([A-Za-z_][A-Za-z0-9_]*)" on type "([A-Za-z_][A-Za-z0-9_]*)"\./;
const UNKNOWN_TYPE = /^Unknown type "HooligansImage(?:Media|Rendition)"/;

/**
 * True when every error is a schema-validation refusal and at least one of
 * them is about `media` or its types: the request would pass without the
 * field. Any other error (network, resolver, an unrelated unknown field) is
 * not, and takes the caller's ordinary failure path.
 */
export function isProductMediaValidationError(errors: unknown): boolean {
  const list = Array.isArray(errors) ? errors : [];
  if (!list.length) return false;
  for (const item of list) {
    const message = String((item as any)?.message || "").trim();
    if (UNKNOWN_TYPE.test(message)) continue;
    const unknownField = message.match(UNKNOWN_FIELD);
    if (!unknownField || !MEDIA_TYPES.test(unknownField[2])) return false;
    // On the two product types only `media` itself qualifies; another
    // unknown field there is somebody else's contract.
    if (/^(?:Product|productImage)$/.test(unknownField[2])) {
      if (unknownField[1] !== "media") return false;
    }
  }
  return true;
}

// Only a request that went out and failed says something about the schema.
// One its caller cancelled (a newer search term) or one that never went out
// (the SSR render that started it was disposed while it waited for the gate,
// so its client is gone) leaves the gate alone.
const isAbortError = (error: any) =>
  error?.name === "AbortError" || error?.networkError?.name === "AbortError";
const isRequestFailure = (error: any) =>
  !!error &&
  !isAbortError(error) &&
  (Array.isArray(error.graphQLErrors) || !!error.networkError);

/**
 * Sends a request whose document selects `media` through the gate: without
 * the field while the schema lacks it; with it otherwise, and once more
 * without it when that request is refused for the field or fails without a
 * readable answer (the gate then closes). An answer that was read (data
 * and/or other errors) is the request's own. `send` issues the request for a
 * document; `errorsOf` reads the GraphQL errors of an answer. `probe: false`:
 * see mediaAllowed.
 */
export async function sendWithProductMedia<T>(
  client: any,
  document: DocumentNode,
  send: (document: DocumentNode) => Promise<T>,
  errorsOf: (result: T) => unknown,
  options?: ProductMediaGateOptions
): Promise<T> {
  if (!(await mediaAllowed(client, options)))
    return send(withoutProductMedia(document));
  let result: T | null = null;
  try {
    result = await send(document);
  } catch (error) {
    if (!isRequestFailure(error)) throw error;
    if (isProductMediaValidationError(apolloErrorList(error)))
      markProductMediaUnsupported();
    else markProductMediaFailure();
  }
  if (result && isProductMediaValidationError(errorsOf(result))) {
    markProductMediaUnsupported();
    result = null;
  }
  return result || send(withoutProductMedia(document));
}

/**
 * The gate for a mutation whose result selects `media` (the bag lines of
 * addToCart, updateCart, removeCart). Unlike a query, the mutation goes out a
 * second time, without the field, only when the first one was refused by
 * schema validation for `media`: GraphQL validates the whole document before
 * any resolver runs, so nothing was done. Any other failure is the mutation's
 * own and is never resent, because the server may have run it (a second
 * addToCart would add the line twice); it still closes the gate briefly, as a
 * query's failure does. A shopper's tap never waits for the probe: an unknown
 * gate counts as open (mediaAllowed with `probe: false`).
 */
export async function sendMutationWithProductMedia<T>(
  client: any,
  document: DocumentNode,
  send: (document: DocumentNode) => Promise<T>,
  errorsOf: (result: T) => unknown
): Promise<T> {
  if (!(await mediaAllowed(client, { probe: false })))
    return send(withoutProductMedia(document));
  let result: T;
  try {
    result = await send(document);
  } catch (error) {
    if (isRequestFailure(error)) {
      if (!isProductMediaValidationError(apolloErrorList(error))) {
        markProductMediaFailure();
        throw error;
      }
      markProductMediaUnsupported();
      return send(withoutProductMedia(document));
    }
    throw error;
  }
  if (isProductMediaValidationError(errorsOf(result))) {
    markProductMediaUnsupported();
    return send(withoutProductMedia(document));
  }
  return result;
}

/** Errors carried by a thrown Apollo error (GraphQL or HTTP-level). */
export function apolloErrorList(error: any): unknown[] {
  return [
    ...(Array.isArray(error?.graphQLErrors) ? error.graphQLErrors : []),
    ...(Array.isArray(error?.networkError?.result?.errors)
      ? error.networkError.result.errors
      : []),
  ];
}

const MEMBER_MEDIA_REFUSAL =
  /^Cannot query field "media" on type "ModelVariantMember"\./;

/**
 * True when every error is a schema-validation refusal of the media
 * selection in the model-variants request: member `media` itself, or the
 * product media types under it. The request would pass without the field.
 */
export function isVariantMemberMediaValidationError(errors: unknown): boolean {
  const list = Array.isArray(errors) ? errors : [];
  if (!list.length) return false;
  return list.every((item) => {
    const message = String((item as any)?.message || "").trim();
    return (
      MEMBER_MEDIA_REFUSAL.test(message) ||
      isProductMediaValidationError([item])
    );
  });
}

/** True when the refusal was read; false: an unreadable failure. */
const noteVariantMemberMediaRefusal = (errors: unknown): boolean => {
  if (isProductMediaValidationError(errors)) markProductMediaUnsupported();
  else if (isVariantMemberMediaValidationError(errors))
    markVariantMemberMediaUnsupported();
  else {
    markVariantMemberMediaFailure();
    return false;
  }
  return true;
};

/**
 * Sends the model-variants request: `withMedia` (member `media` selected)
 * only while knownVariantMemberMediaSupport() is true, else `withoutMedia`.
 * When `withMedia` is refused for the field or fails without a readable
 * answer, `withoutMedia` goes out at once (a query: nothing was changed) and
 * the member answer closes: until the next probe after a refusal, and after
 * a failure too once `withoutMedia` has passed. The live connector answers a
 * refusal with an unreadable var_dump, so on production a removed field shows
 * up as exactly that: a failure with the field, an answer without it. An
 * answer that was read is the request's own.
 */
export async function sendWithVariantMemberMedia<T>(
  withMedia: DocumentNode,
  withoutMedia: DocumentNode,
  send: (document: DocumentNode) => Promise<T>,
  errorsOf: (result: T) => unknown
): Promise<T> {
  if (knownVariantMemberMediaSupport() !== true) return send(withoutMedia);
  let result: T | null = null;
  let unreadable = false;
  try {
    result = await send(withMedia);
  } catch (error) {
    if (!isRequestFailure(error)) throw error;
    unreadable = !noteVariantMemberMediaRefusal(apolloErrorList(error));
  }
  if (result && isVariantMemberMediaValidationError(errorsOf(result))) {
    noteVariantMemberMediaRefusal(errorsOf(result));
    result = null;
  }
  if (result) return result;
  const answer = await send(withoutMedia);
  if (unreadable) markVariantMemberMediaUnsupported();
  return answer;
}

const stripped = new WeakMap<DocumentNode, DocumentNode>();

/** The same document with every `media` selection removed (memoized). */
export function withoutProductMedia(document: DocumentNode): DocumentNode {
  if (!document || typeof document !== "object") return document;
  const cached = stripped.get(document);
  if (cached) return cached;
  const result = visit(document, {
    Field(node) {
      return node.name.value === "media" ? null : undefined;
    },
  }) as DocumentNode;
  // graphql-tag keeps the original source text on `loc`; drop it so nothing
  // that prints from `loc` can resend the stripped field.
  const clean = { ...result, loc: undefined } as unknown as DocumentNode;
  stripped.set(document, clean);
  return clean;
}
